/*
 * @providesModule SMSPhoneInput
 */

import React, {useState} from 'react';
import {ImageStyle, Pressable, Text, TextInput, View} from 'react-native';
import {errorColor, defaultColor, getStyles} from 'SMSVerifyStyle';
import SkillzSVG from 'SkillzSVG';
import {_m} from 'INTL';
import SkillzStyle from 'SkillzStyle';

export type SMSEvents = {
  // Event for pressing countrySelectBox
  countrySelect: string;
  //Event for when code is requested
  codeRequest: string;
  //Event for call request
  callRequest: string;
  //Event for Entering SMS Code
  codeEnter: string;
};

export type MessageLabel = {
  body?: string;
  header: string;
};

export type SMSPhoneInputProps = {
  /**
   *
   * @param country Country used to get flag
   * @param style Passed in to given function to style flag
   * @returns flag image
   */
  getFlag: (country: string, style: ImageStyle) => React.ReactElement;
  // Value to showCountryListModal
  showCountryList: {
    value: boolean; // value for showCountryList
    set: React.Dispatch<React.SetStateAction<boolean>>; //set function to change value for phoneNumber
  };
  // Country in iso2 format
  country: string;
  //country code for phone number
  countryValue: string;
  // function to track events used for AWS events
  trackEvent: (name: string, params?: Object, metrics?: Object) => void;
  //Text used for head and body
  messageText: MessageLabel;
  // phone number string of user
  phoneNumber: {
    value: string; // value for phoneNumber
    set: React.Dispatch<React.SetStateAction<string>>; //set function to change value for phoneNumber
  };
  // Value to show error styles or not
  errorStyle: boolean;
  // Value to show error styles or not
  errorMessage?: string;
  /**  Function used to format input for the phone number input box*/
  formatNumber: (number: string, iso2: string) => string;
  // events used for trackEvent
  events: SMSEvents;
  autoFocus?: boolean;
  onFocus?: () => void;
  colorVariant?: 'dark';
  useSmallSize?: boolean;
  isNewUI?: boolean;
};

export const SMSPhoneInput = ({
  getFlag,
  showCountryList,
  country,
  countryValue,
  trackEvent,
  messageText,
  phoneNumber,
  errorStyle,
  formatNumber,
  events,
  autoFocus,
  onFocus,
  colorVariant,
  useSmallSize = false,
  errorMessage,
  isNewUI,
}: SMSPhoneInputProps) => {
  const labelText = _m('Phone Number');

  const TEST_IDS = {
    countryBox: 'CountryBox',
  };
  const [inputPhoneFocus, setPhoneFocus] = useState<boolean>(false);
  const styles = getStyles(colorVariant, useSmallSize);
  const tintColor = colorVariant === 'dark' ? '#fff' : undefined;

  return (
    <View style={!isNewUI && {paddingHorizontal: 20}}>
      <View>
        <Text
          allowFontScaling={false}
          style={[
            isNewUI ? styles.newMessageHeader : styles.messageHeader,
            isNewUI && {color: SkillzStyle.fullStyles.textColorPrimary.color},
          ]}
        >
          {messageText.header}
        </Text>
        {messageText.body ? (
          <Text
            allowFontScaling={false}
            style={[
              isNewUI ? styles.newMessageBody : styles.messageBody,
              isNewUI && {color: SkillzStyle.fullStyles.textColorPrimary.color},
            ]}
          >
            {messageText.body}
          </Text>
        ) : null}
      </View>
      <View style={styles.inputContainer}>
        <Pressable
          onPress={() => {
            trackEvent(events.countrySelect);
            showCountryList.set(true);
          }}
          testID={TEST_IDS.countryBox}
        >
          <View
            style={[
              styles.countryBoxContainer,
              colorVariant === 'dark'
                ? {
                    borderColor: errorStyle
                      ? errorColor
                      : autoFocus || inputPhoneFocus
                        ? styles.phoneInputFocus.borderColor
                        : styles.phoneInput.borderColor,
                    borderWidth: autoFocus || inputPhoneFocus ? 2 : styles.phoneInput.borderWidth,
                  }
                : {},
            ]}
          >
            <View style={styles.countryBoxFlagContainer}>
              {getFlag(country, styles.countryBoxFlag)}
              <Text
                allowFontScaling={false}
                style={styles.countryBoxText}
              >{`${countryValue}`}</Text>
            </View>
            <View style={[styles.countryBoxArrowContainer, isNewUI && {top: 10}]}>
              <SkillzSVG
                source={'ic_arrow_drop_down.svg'}
                style={styles.countryBoxArrow}
                tintColor={tintColor}
                resizeMode='stretch'
              />
            </View>
          </View>
        </Pressable>
        <View style={styles.phoneInputContainer}>
          <View
            style={[
              styles.phoneInput,
              {
                borderColor: errorStyle
                  ? errorColor
                  : autoFocus || inputPhoneFocus
                    ? styles.phoneInputFocus.borderColor
                    : styles.phoneInput.borderColor,
                borderWidth: autoFocus || inputPhoneFocus ? 2 : styles.phoneInput.borderWidth,
              },
            ]}
          >
            {phoneNumber?.value?.length ? (
              <Text
                allowFontScaling={false}
                style={[
                  styles.labelPhoneInput,
                  {color: errorStyle ? errorColor : styles.labelPhoneInput.color},
                ]}
              >
                {labelText}
              </Text>
            ) : null}

            <TextInput
              placeholder={labelText}
              style={
                phoneNumber?.value?.length
                  ? {...styles.phoneInputBox, ...styles.phoneInputBoxStyle}
                  : {...styles.phoneInputBox, ...styles.phoneInputBoxPrefill}
              }
              placeholderTextColor={styles.placeholderTextColor.color}
              selectionColor={errorStyle ? errorColor : defaultColor}
              inputMode='tel'
              onChangeText={(text: string) => {
                return phoneNumber.set(formatNumber(text, country));
              }}
              onFocus={() => {
                setPhoneFocus(true);
                onFocus ? onFocus() : null;
              }}
              onBlur={() => {
                setPhoneFocus(false);
              }}
              autoFocus={autoFocus ? autoFocus : true}
              value={phoneNumber.value}
              textContentType='telephoneNumber'
              cursorColor={
                SkillzStyle.getOverriddenFallbackTheme().playButtonGradient[1] ?? '#4890e8'
              }
            />
            {errorStyle && errorMessage ? (
              <View style={styles.errorMessage}>
                <SkillzSVG
                  source={'icon_info_filled.svg'}
                  style={styles.errorInfo}
                  tintColor={errorColor}
                />
                <Text
                  allowFontScaling={false}
                  style={{
                    color: errorStyle ? errorColor : defaultColor,
                    ...styles.errorText,
                  }}
                >
                  {errorMessage}
                </Text>
              </View>
            ) : null}
          </View>
        </View>
      </View>
    </View>
  );
};
